import { useQuery } from '@tanstack/react-query';
import { LayoutGrid, Table2 } from 'lucide-react';
import { useId, useMemo, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';

import { Select } from '@/components/ui/Select';
import { usePlatformPermissions } from '@/features/auth/hooks/usePlatformPermissions';
import { listTenants, type TenantEdge } from '@/features/tenants/api/tenants.graphql';
import { OnboardingProgress } from '@/features/tenants/components/OnboardingProgress';
import { useModuleCatalog } from '@/features/tenants/hooks/useModuleCatalog';
import { formatLifecycleDate, statusLabel } from '@/features/tenants/tenant-labels';
import { formatMoney } from '@/lib/money';

import { fichaLink } from './AttentionPanel';
import { HealthBadge } from './HealthBadge';
import styles from './TenantOverview.module.scss';
import type { TenantHealthScore } from '../api/metrics.graphql';
import { HEALTH_DEFINITION, inactiveForAWeek, mainAlert } from '../health-labels';

/** Con menos de esta cantidad de tenants la vista por defecto son tarjetas. */
export const CARDS_UNDER = 20;
const STATUS_OPTIONS = ['pilot', 'trial', 'active', 'past_due', 'suspended', 'cancelled'] as const;
type SortKey = 'createdAt' | 'name' | 'mrr' | 'health';

interface Props {
  health: TenantHealthScore[] | undefined;
}

/**
 * OS-1724 — panorama de todos los tenants: tarjetas (por defecto con menos de 20) o tabla densa,
 * filtros y orden en el cliente sobre el listado existente (`platformTenants`, hasta 100) y la salud
 * (`tenantHealthScores`). El MRR sale del mismo listado (OS-1723); sin `billing.read` no se muestra.
 * Cada tenant lleva a su ficha en un clic, con la pestaña en la URL.
 */
export function TenantOverview({ health }: Props): JSX.Element {
  const idPrefix = useId();
  const { can } = usePlatformPermissions();
  const canBilling = can('platform.billing.read');
  const tenantsQ = useQuery({
    queryKey: ['platform-tenants', 'dashboard-overview'],
    queryFn: () => listTenants({ take: 100 }),
    refetchInterval: 5 * 60 * 1000,
  });
  const catalog = useModuleCatalog();
  const moduleNames = new Map((catalog.data ?? []).map((module) => [module.key, module.name]));
  const healthById = useMemo(() => new Map((health ?? []).map((item) => [item.tenantId, item])), [health]);

  const tenants = tenantsQ.data?.edges ?? [];
  const [view, setView] = useState<'cards' | 'table' | null>(null);
  const currentView = view ?? (tenants.length < CARDS_UNDER ? 'cards' : 'table');
  const [moduleKey, setModuleKey] = useState('');
  const [status, setStatus] = useState('');
  const [incomplete, setIncomplete] = useState(false);
  const [inactive, setInactive] = useState(false);
  const [sort, setSort] = useState<SortKey>('createdAt');

  const now = new Date();
  const visible = tenants
    .filter((tenant) => !moduleKey || (tenant.enabledModules ?? []).includes(moduleKey))
    .filter((tenant) => !status || tenant.planStatus === status)
    .filter((tenant) => !incomplete || (tenant.onboarding ? !tenant.onboarding.complete : false))
    .filter((tenant) => !inactive || inactiveForAWeek(healthById.get(tenant.id), now) === true)
    .sort((a, b) => compare(a, b, sort, healthById));

  const field = (name: string): string => `${idPrefix}-${name}`;

  return (
    <section className={styles.overview} aria-labelledby={field('title')}>
      <div className={styles.header}>
        <h2 id={field('title')} className={styles.title}>
          Tenants
        </h2>
        <div className={styles.viewToggle} role="group" aria-label="Vista">
          <button
            type="button"
            aria-pressed={currentView === 'cards'}
            className={currentView === 'cards' ? styles.toggleOn : styles.toggle}
            onClick={() => setView('cards')}
          >
            <LayoutGrid size={14} aria-hidden="true" /> Tarjetas
          </button>
          <button
            type="button"
            aria-pressed={currentView === 'table'}
            className={currentView === 'table' ? styles.toggleOn : styles.toggle}
            onClick={() => setView('table')}
          >
            <Table2 size={14} aria-hidden="true" /> Tabla
          </button>
        </div>
      </div>
      {health ? <p className={styles.definition}>{HEALTH_DEFINITION}</p> : null}

      <div className={styles.filters}>
        <label className={styles.field} htmlFor={field('module')}>
          Módulo
          <Select
            id={field('module')}
            value={moduleKey}
            onChange={setModuleKey}
            options={[
              { value: '', label: 'Todos' },
              ...(catalog.data ?? []).map((module) => ({ value: module.key, label: module.name })),
            ]}
          />
        </label>
        <label className={styles.field} htmlFor={field('status')}>
          Estado
          <Select
            id={field('status')}
            value={status}
            onChange={setStatus}
            options={[
              { value: '', label: 'Todos' },
              ...STATUS_OPTIONS.map((option) => ({ value: option, label: statusLabel(option) })),
            ]}
          />
        </label>
        <label className={styles.field} htmlFor={field('sort')}>
          Ordenar por
          <Select
            id={field('sort')}
            value={sort}
            onChange={setSort}
            options={[
              { value: 'createdAt', label: 'Alta (recientes primero)' },
              { value: 'name', label: 'Nombre' },
              ...(canBilling ? [{ value: 'mrr' as const, label: 'MRR (mayor primero)' }] : []),
              ...(health ? [{ value: 'health' as const, label: 'Salud (peor primero)' }] : []),
            ]}
          />
        </label>
        <label className={styles.check}>
          <input type="checkbox" checked={incomplete} onChange={(e) => setIncomplete(e.target.checked)} />
          Onboarding incompleto
        </label>
        {health ? (
          <label className={styles.check}>
            <input type="checkbox" checked={inactive} onChange={(e) => setInactive(e.target.checked)} />
            Sin actividad en 7 días
          </label>
        ) : null}
      </div>
      {inactive ? (
        <p className={styles.note}>
          Los tenants sin registros de uso no se cuentan como inactivos: no se sabe (ver "Sin datos de uso").
        </p>
      ) : null}

      {tenantsQ.isLoading ? (
        <p className={styles.muted}>Cargando tenants…</p>
      ) : tenantsQ.isError ? (
        <p className={styles.error} role="alert">
          No se pudieron cargar los tenants: {tenantsQ.error.message}
        </p>
      ) : visible.length === 0 ? (
        <p className={styles.muted}>Ningún tenant cumple estos filtros.</p>
      ) : currentView === 'cards' ? (
        <ul className={styles.cards} aria-label="Tenants">
          {visible.map((tenant) => (
            <TenantCard
              key={tenant.id}
              tenant={tenant}
              health={healthById.get(tenant.id)}
              moduleNames={moduleNames}
              showMrr={canBilling}
              showHealth={Boolean(health)}
            />
          ))}
        </ul>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <caption className={styles.srOnly}>Tenants</caption>
            <thead>
              <tr>
                <th scope="col">Tenant</th>
                <th scope="col">Estado</th>
                <th scope="col">Módulos</th>
                {canBilling ? <th scope="col">MRR (COP)</th> : null}
                {health ? <th scope="col">Salud</th> : null}
                <th scope="col">Onboarding</th>
                <th scope="col">Alerta</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((tenant) => {
                const tenantHealth = healthById.get(tenant.id);
                const alert = mainAlert(tenant, tenantHealth);
                return (
                  <tr key={tenant.id}>
                    <th scope="row">
                      <Link to={fichaLink(tenant.id, 'summary')}>{tenant.name}</Link>
                    </th>
                    <td>{statusLabel(tenant.planStatus)}</td>
                    <td>{(tenant.enabledModules ?? []).map((key) => moduleNames.get(key) ?? key).join(', ')}</td>
                    {canBilling ? <td>{mrrText(tenant)}</td> : null}
                    {health ? <td>{tenantHealth ? <HealthBadge health={tenantHealth} /> : '—'}</td> : null}
                    <td>{tenant.onboarding ? `${tenant.onboarding.percent} %` : '—'}</td>
                    <td>
                      {alert ? (
                        <Link to={fichaLink(tenant.id, alert.tab)} className={styles[alert.tone]}>
                          {alert.label}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function mrrText(tenant: TenantEdge): string {
  if (typeof tenant.potentialMrr === 'number' && tenant.potentialMrr > 0) {
    return `${formatMoney(tenant.potentialMrr)} potencial`;
  }
  return formatMoney(tenant.mrr ?? null);
}

/** Orden estable; los valores desconocidos (sin MRR o sin puntaje) van al final. */
function compare(
  a: TenantEdge,
  b: TenantEdge,
  sort: SortKey,
  health: ReadonlyMap<string, TenantHealthScore>,
): number {
  const tie = a.name.localeCompare(b.name, 'es');
  if (sort === 'name') {return tie;}
  if (sort === 'createdAt') {return b.createdAt.localeCompare(a.createdAt) || tie;}
  if (sort === 'mrr') {
    const value = (t: TenantEdge): number => (t.mrr ?? 0) + (t.potentialMrr ?? 0);
    return value(b) - value(a) || tie;
  }
  const score = (t: TenantEdge): number => health.get(t.id)?.healthScore ?? Number.POSITIVE_INFINITY;
  return score(a) - score(b) || tie;
}

interface CardProps {
  tenant: TenantEdge;
  health: TenantHealthScore | undefined;
  moduleNames: ReadonlyMap<string, string>;
  showMrr: boolean;
  showHealth: boolean;
}

function TenantCard({ tenant, health, moduleNames, showMrr, showHealth }: CardProps): JSX.Element {
  const alert = mainAlert(tenant, health);
  return (
    <li className={styles.card}>
      <div className={styles.cardHeader}>
        <Link to={fichaLink(tenant.id, 'summary')} className={styles.cardTitle}>
          {tenant.name}
        </Link>
        <span className={styles.status}>{statusLabel(tenant.planStatus)}</span>
      </div>
      <ul className={styles.chips} aria-label={`Módulos de ${tenant.name}`}>
        {(tenant.enabledModules ?? []).map((key) => (
          <li key={key} className={styles.chip}>
            {moduleNames.get(key) ?? key}
          </li>
        ))}
      </ul>
      <dl className={styles.facts}>
        {showMrr ? (
          <div>
            <dt>MRR</dt>
            <dd>{mrrText(tenant)}</dd>
          </div>
        ) : null}
        {showHealth ? (
          <div>
            <dt>Salud</dt>
            <dd>{health ? <HealthBadge health={health} /> : '—'}</dd>
          </div>
        ) : null}
        <div>
          <dt>Alta</dt>
          <dd>{formatLifecycleDate(tenant.createdAt)}</dd>
        </div>
      </dl>
      {tenant.onboarding ? (
        <OnboardingProgress onboarding={tenant.onboarding} label={`Onboarding de ${tenant.name}`} />
      ) : null}
      {alert ? (
        <Link to={fichaLink(tenant.id, alert.tab)} className={`${styles.alert} ${styles[alert.tone]}`}>
          {alert.label}
        </Link>
      ) : null}
    </li>
  );
}
